import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { supabase } from '../../../../lib/supabase';
import { Client } from '../../../../context/ClientContext';
import { validateImageUrl } from './mediaUtils';
import { Button, SidePanel } from '../../../ui';

interface ImageUrlModalProps {
  isOpen: boolean;
  onClose: () => void;
  client?: Client;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

interface UploadResult {
  url: string;
  success: boolean;
  error?: string;
}

export default function ImageUrlModal({ isOpen, onClose, client, onSuccess, onError }: ImageUrlModalProps) {
  const [imageUrls, setImageUrls] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([]);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  const handleSubmit = async () => {
    if (!client?.id || !imageUrls.trim()) {
      setUrlError('Please enter at least one image URL');
      return;
    }

    const urls = imageUrls
      .split('\n')
      .map((url) => url.trim())
      .filter((url) => url.length > 0);

    if (urls.length === 0) {
      setUrlError('Please enter at least one valid image URL');
      return;
    }

    const validationErrors: string[] = [];
    urls.forEach((url, index) => {
      const error = validateImageUrl(url);
      if (error) {
        validationErrors.push(`Line ${index + 1}: ${error}`);
      }
    });

    if (validationErrors.length > 0) {
      setUrlError(validationErrors.join('\n'));
      return;
    }

    setIsUploading(true);
    setUrlError(null);
    setUploadResults([]);
    setUploadProgress({ current: 0, total: urls.length });

    const results: UploadResult[] = [];

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No authentication session');

      for (let i = 0; i < urls.length; i++) {
        const url = urls[i];
        setUploadProgress({ current: i + 1, total: urls.length });

        try {
          const response = await fetch('/.netlify/functions/r2-media-manager', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              clientId: client.id,
              imageUrl: url,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Upload failed');
          }

          results.push({ url, success: true });
        } catch (err) {
          results.push({
            url,
            success: false,
            error: err instanceof Error ? err.message : 'Unknown error',
          });
        }
      }

      setUploadResults(results);

      const successCount = results.filter((r) => r.success).length;
      const failCount = results.filter((r) => !r.success).length;

      if (successCount > 0 && failCount === 0) {
        setTimeout(() => {
          handleClose();
          onSuccess(`Successfully uploaded ${successCount} image${successCount > 1 ? 's' : ''}!`);
        }, 2000);
      } else if (successCount > 0 && failCount > 0) {
        onSuccess(`Uploaded ${successCount} image${successCount > 1 ? 's' : ''}, ${failCount} failed`);
      } else {
        onError('All uploads failed. Please check the URLs and try again.');
      }
    } catch (err) {
      console.error('Error uploading images from URLs:', err);
      onError(`Failed to upload images: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsUploading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  const handleClose = () => {
    onClose();
    setImageUrls('');
    setUrlError(null);
    setUploadResults([]);
    setUploadProgress({ current: 0, total: 0 });
  };

  const handleUrlChange = (value: string) => {
    setImageUrls(value);
    if (urlError) setUrlError(null);
  };

  return (
    <SidePanel
      open={isOpen}
      onClose={handleClose}
      width={460}
      eyebrow="Media"
      title="Upload Images from URLs"
      footer={
        <>
          <Button icon="plus" onClick={handleSubmit} disabled={isUploading || !imageUrls.trim()}>
            {isUploading ? 'Uploading...' : 'Upload Images'}
          </Button>
          <Button variant="ghost" onClick={handleClose} disabled={isUploading}>
            Cancel
          </Button>
        </>
      }
    >
      <div className="ck-stagger" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Image URLs — one per line</div>
          <textarea
            value={imageUrls}
            onChange={(e) => handleUrlChange(e.target.value)}
            placeholder={'https://example.com/image1.jpg\nhttps://example.com/image2.jpg'}
            rows={6}
            className="ck-textarea t-num"
            style={{ fontSize: 'var(--text-xs)' }}
            disabled={isUploading}
          />
          {urlError && (
            <p
              style={{
                margin: '6px 0 0',
                fontSize: 'var(--text-xs)',
                color: 'var(--danger-400)',
                whiteSpace: 'pre-line',
              }}
            >
              {urlError}
            </p>
          )}
        </div>

        {isUploading && uploadProgress.total > 0 && (
          <div
            style={{
              padding: '12px 14px',
              background: 'var(--surface-raised)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <p className="t-num" style={{ margin: '0 0 8px', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
              Uploading {uploadProgress.current} of {uploadProgress.total}...
            </p>
            <div
              style={{
                height: '3px',
                borderRadius: '2px',
                background: 'var(--surface-active)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${(uploadProgress.current / uploadProgress.total) * 100}%`,
                  height: '100%',
                  borderRadius: '2px',
                  background: 'var(--accent)',
                  transition: 'width var(--dur-base) var(--ease-out)',
                }}
              />
            </div>
          </div>
        )}

        {uploadResults.length > 0 && (
          <div
            style={{
              padding: '12px 14px',
              maxHeight: '160px',
              overflowY: 'auto',
              background: 'var(--surface-raised)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <div className="t-eyebrow" style={{ marginBottom: '8px' }}>Results</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {uploadResults.map((result, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <FontAwesomeIcon
                    icon={result.success ? faCircleCheck : faCircleXmark}
                    style={{
                      fontSize: '12px',
                      marginTop: '2px',
                      flexShrink: 0,
                      color: result.success ? 'var(--status-up)' : 'var(--status-down)',
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p
                      className="t-num"
                      style={{
                        margin: 0,
                        fontSize: 'var(--text-xs)',
                        color: 'var(--text-muted)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {result.url}
                    </p>
                    {result.error && (
                      <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--danger-400)' }}>{result.error}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--text-faint)', lineHeight: 1.5 }}>
          Supported formats: JPEG, PNG, GIF, WebP, SVG up to 50 MB. Paste multiple URLs — one per line — to bulk
          upload.
        </p>
      </div>
    </SidePanel>
  );
}
